import { CONTROLS } from './constants';
import {
  InputCommand,
  InputCommandQueue,
  InputCommandType,
  createMoveCommand,
  createTakeActionCommand,
  createBuildDeconstructCommand,
  createToggleUICommand,
  createCameraCommand,
  createSimpleActionCommand,
  createCollectResourceCommand,
} from './InputCommands';
import { inputLogger } from './Logger';

// Key state tracking for individual keys
interface KeyState {
  pressed: boolean;
  justPressed: boolean; // True only on the first frame when key becomes pressed
  justReleased: boolean; // True only on the first frame when key becomes released
  isRepeat: boolean; // True if the current key press is from a repeat event
}

// Singleton InputManager class
export class InputManager {
  private static instance: InputManager | null = null;

  // Key state tracking
  private keyStates: Map<string, KeyState> = new Map();

  // Separate command queues for UI and game logic
  private uiCommandQueue: InputCommandQueue = new InputCommandQueue();
  private gameCommandQueue: InputCommandQueue = new InputCommandQueue();

  // Event listeners state
  private isListening = false;

  // Singleton accessor
  static getInstance(): InputManager {
    if (!InputManager.instance) {
      InputManager.instance = new InputManager();
    }
    return InputManager.instance;
  }

  private constructor() {
    // Private constructor for singleton
  }

  // Helper to determine if a command is a UI command
  private isUICommand(command: InputCommand): boolean {
    switch (command.type) {
      // Menu toggles are now handled by GameEngine
      case InputCommandType.SAVE_GAME: // UI handles save menu
      case InputCommandType.TOGGLE_INVENTORY:
      case InputCommandType.CLOSE_ALL_MENUS:
        return true;
      default:
        return false;
    }
  }

  // Enqueue command to appropriate queue
  private enqueueCommand(command: InputCommand): void {
    const isUI = this.isUICommand(command);
    if (command.type === InputCommandType.TAKE_ACTION) {
      inputLogger.debug('Routing TAKE_ACTION command', {
        command,
        routedToUI: isUI,
      });
    }

    if (isUI) {
      this.uiCommandQueue.enqueue(command);
    }

    if (!isUI || command.type === InputCommandType.CLOSE_ALL_MENUS) {
      this.gameCommandQueue.enqueue(command);
    }
  }

  // Start listening to keyboard events
  startListening(): void {
    if (this.isListening) return;

    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.handleWindowBlur);

    this.isListening = true;
  }

  // Stop listening to keyboard events
  stopListening(): void {
    if (!this.isListening) return;

    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleWindowBlur);

    this.isListening = false;
  }

  // Handle keydown events
  private handleKeyDown = (e: KeyboardEvent): void => {
    // Prevent default for arrow keys to avoid page scrolling
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }

    // Don't process keys if an input field is focused
    const activeElement = document.activeElement;
    const isInputFocused =
      activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT');

    if (isInputFocused) return;

    // Update key state
    const currentState = this.keyStates.get(e.key) || {
      pressed: false,
      justPressed: false,
      justReleased: false,
      isRepeat: false,
    };

    // Only update if not already pressed (prevents repeat events from resetting justPressed)
    if (!currentState.pressed) {
      this.keyStates.set(e.key, {
        pressed: true,
        justPressed: true,
        justReleased: false,
        isRepeat: false,
      });
    } else if (e.repeat) {
      // Update repeat status for already pressed keys
      this.keyStates.set(e.key, {
        ...currentState,
        isRepeat: true,
      });
    }

    // Generate immediate commands for certain keys (non-repeating actions)
    this.generateImmediateCommands(e);
  };

  // Handle keyup events
  private handleKeyUp = (e: KeyboardEvent): void => {
    // Update key state
    this.keyStates.set(e.key, {
      pressed: false,
      justPressed: false,
      justReleased: true,
      isRepeat: false,
    });
  };

  // Handle window blur to clear all key states
  private handleWindowBlur = (): void => {
    // Clear all pressed keys when window loses focus
    this.keyStates.forEach((state, key) => {
      if (state.pressed) {
        this.keyStates.set(key, {
          pressed: false,
          justPressed: false,
          justReleased: true,
          isRepeat: false,
        });
      }
    });
  };

  // Generate immediate commands for non-repeating actions
  private generateImmediateCommands(e: KeyboardEvent): void {
    // Building status menu toggle
    if (CONTROLS.BUILD_MENU.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_BUILD_MENU)
      );
      return;
    }

    if (CONTROLS.RESEARCH_MENU.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_RESEARCH_MENU)
      );
      return;
    }

    if (CONTROLS.PRODUCTION_MENU.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_PRODUCTION_MENU)
      );
      return;
    }

    if (CONTROLS.INVENTORY.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_INVENTORY)
      );
      return;
    }

    // Cancel/Escape actions
    if (CONTROLS.CANCEL.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.CLOSE_ALL_MENUS)
      );
      return;
    }

    // Take action (contextual based on game state)
    if (CONTROLS.TAKE_ACTION.includes(e.key)) {
      const command = createTakeActionCommand({ shiftKey: e.shiftKey });
      inputLogger.debug('Generated TAKE_ACTION command', {
        key: e.key,
        shiftKey: e.shiftKey,
        command,
      });
      this.enqueueCommand(command);
      return;
    }

    if (CONTROLS.COLLECT_RESOURCE.includes(e.key)) {
      this.enqueueCommand(createCollectResourceCommand());
      return;
    }

    // Building deconstruction
    if (CONTROLS.BUILD_DECONSTRUCT.includes(e.key)) {
      this.enqueueCommand(createBuildDeconstructCommand());
      return;
    }

    // Zoom controls
    if (CONTROLS.ZOOM_IN.includes(e.key)) {
      this.enqueueCommand(createCameraCommand(InputCommandType.ZOOM_IN));
      return;
    }

    if (CONTROLS.ZOOM_OUT.includes(e.key)) {
      this.enqueueCommand(createCameraCommand(InputCommandType.ZOOM_OUT));
      return;
    }

    if (CONTROLS.ZOOM_RESET.includes(e.key)) {
      this.enqueueCommand(createCameraCommand(InputCommandType.ZOOM_RESET));
      return;
    }

    // Movement commands
    let moveX = 0;
    let moveY = 0;

    if (CONTROLS.MOVE_UP.includes(e.key)) moveY = -1;
    else if (CONTROLS.MOVE_DOWN.includes(e.key)) moveY = 1;

    if (CONTROLS.MOVE_LEFT.includes(e.key)) moveX = -1;
    else if (CONTROLS.MOVE_RIGHT.includes(e.key)) moveX = 1;

    if (moveX !== 0 || moveY !== 0) {
      this.enqueueCommand(createMoveCommand(moveX, moveY, e.repeat));
      return;
    }

    // Save game
    if (CONTROLS.SAVE_GAME.includes(e.key) || (e.key === 's' && e.shiftKey)) {
      this.enqueueCommand(
        createSimpleActionCommand(InputCommandType.SAVE_GAME)
      );
      return;
    }

    // Clear belt items debug command
    if (e.key === 'c' || e.key === 'C') {
      this.enqueueCommand(
        createSimpleActionCommand(InputCommandType.CLEAR_BELT_ITEMS)
      );
      return;
    }

    // Save menu
    if (CONTROLS.SAVE_MENU.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_SAVE_MENU)
      );
      return;
    }

    // Debug menu
    if (CONTROLS.DEBUG_MENU.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_DEBUG_MENU)
      );
      return;
    }

    // Achievement menu
    if (CONTROLS.ACHIEVEMENT_MENU.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_ACHIEVEMENT_MENU)
      );
      return;
    }

    // Help menu
    if (CONTROLS.HELP_MENU.includes(e.key)) {
      this.enqueueCommand(
        createToggleUICommand(InputCommandType.TOGGLE_HELP_MENU)
      );
      return;
    }
  }

  // Update key states and generate repeating commands (called each game tick)
  tick(): void {
    // Update justPressed and justReleased states
    this.keyStates.forEach((state, key) => {
      this.keyStates.set(key, {
        ...state,
        justPressed: false,
        justReleased: false,
      });
    });

    // Generate repeating camera commands
    this.generateRepeatingCameraCommands();
    this.generateRepeatingCollectCommands();
  }

  // Generate repeating camera commands
  private generateRepeatingCameraCommands(): void {
    if (CONTROLS.ZOOM_IN.some(key => this.isKeyPressed(key))) {
      this.enqueueCommand(createCameraCommand(InputCommandType.ZOOM_IN, true));
    }

    if (CONTROLS.ZOOM_OUT.some(key => this.isKeyPressed(key))) {
      this.enqueueCommand(createCameraCommand(InputCommandType.ZOOM_OUT, true));
    }
  }

  private generateRepeatingCollectCommands(): void {
    if (CONTROLS.COLLECT_RESOURCE.some(key => this.isKeyPressed(key))) {
      this.enqueueCommand(createCollectResourceCommand({ repeat: true }));
    }
  }

  // Check if a key is currently pressed
  isKeyPressed(key: string): boolean {
    return this.keyStates.get(key)?.pressed || false;
  }

  // Check if a key was just pressed this frame
  isKeyJustPressed(key: string): boolean {
    return this.keyStates.get(key)?.justPressed || false;
  }

  // Check if a key was just released this frame
  isKeyJustReleased(key: string): boolean {
    return this.keyStates.get(key)?.justReleased || false;
  }

  // Queue a TAKE_ACTION command targeted at a specific tile (e.g., from mouse input)
  triggerTakeActionAt(position: { x: number; y: number }): void {
    const command = createTakeActionCommand({ target: position });
    inputLogger.debug('Generated TAKE_ACTION command from mouse interaction', {
      position,
      command,
    });
    this.enqueueCommand(command);
  }

  // Get all queued game commands and clear the queue
  getGameCommands(): InputCommand[] {
    return this.gameCommandQueue.dequeueAll();
  }

  // Get all queued UI commands and clear the queue
  getUICommands(): InputCommand[] {
    return this.uiCommandQueue.dequeueAll();
  }

  // Clear all queued commands
  clearCommands(): void {
    this.uiCommandQueue.clear();
    this.gameCommandQueue.clear();
  }

  // Allow UI components to trigger commands
  triggerCommand(command: InputCommand): void {
    this.enqueueCommand(command);
  }

  // Get the current state of all keys (for debugging)
  getKeyStates(): ReadonlyMap<string, Readonly<KeyState>> {
    return this.keyStates;
  }
}
